import React, { useState } from 'react';
import { Card, Collapse, Tag, Typography, Space, Divider, Button } from 'antd';
import { CaretRightOutlined, ClearOutlined, CopyOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { message } from 'antd';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface HttpCallLog {
  id: string;
  timestamp: string;
  method: string;
  url: string;
  status: number;
  success: boolean;
  responseTime: number;
  requestHeaders?: any;
  requestBody?: any;
  responseHeaders?: any;
  responseBody?: any;
  error?: string;
}

interface ExecutionLog {
  id: string;
  timestamp: string;
  type: 'info' | 'error' | 'success' | 'warning';
  message: string;
  details?: any;
}

interface ConsoleViewerProps {
  httpCalls: HttpCallLog[];
  executionLogs: ExecutionLog[];
  totalExecutionTime: number;
  visible: boolean;
  onClear: () => void;
}

export const ConsoleViewer: React.FC<ConsoleViewerProps> = ({
  httpCalls,
  executionLogs,
  totalExecutionTime,
  visible,
  onClear
}) => {
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

  if (!visible) return null;

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 300 && status < 400) return 'processing';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'error';
    return 'default';
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'blue';
      case 'POST': return 'green';
      case 'PUT': return 'orange';
      case 'PATCH': return 'purple';
      case 'DELETE': return 'red';
      default: return 'default';
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label} copied to clipboard!`);
    });
  };

  const formatJSON = (obj: any) => {
    if (!obj) return 'N/A';
    if (typeof obj === 'string') {
      try {
        return JSON.stringify(JSON.parse(obj), null, 2);
      } catch {
        return obj;
      }
    }
    return JSON.stringify(obj, null, 2);
  };

  const renderJSONViewer = (data: any, title: string, copyData?: any) => {
    if (!data) return null;

    const jsonString = formatJSON(data);
    const displayData = copyData || data;

    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <Text strong style={{ fontSize: '13px' }}>{title}:</Text>
          <Button
            size="small"
            type="text"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(formatJSON(displayData), title)}
            style={{ marginLeft: 8, padding: '0 4px' }}
            title={`Copy ${title}`}
          />
        </div>
        <div
          style={{
            background: '#f6f6f6',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            padding: '8px',
            fontSize: '12px',
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            overflow: 'auto',
            maxHeight: '300px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {jsonString}
        </div>
      </div>
    );
  };

  const renderHttpCallDetails = (call: HttpCallLog) => (
    <div key={call.id} style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 8 }}>
        <Tag color={getMethodColor(call.method)}>{call.method}</Tag>
        <Tag color={getStatusColor(call.status)}>{call.status}</Tag>
        <Text style={{ fontSize: '12px', color: '#666' }}>
          <ClockCircleOutlined /> {call.responseTime}ms
        </Text>
        <Text style={{ fontSize: '12px', color: '#999' }}>
          {formatTimestamp(call.timestamp)}
        </Text>
      </div>

      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: '13px' }}>URL:</Text>
        <div
          style={{
            background: '#f0f0f0',
            padding: '4px 8px',
            borderRadius: '3px',
            fontSize: '12px',
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            marginTop: 4,
            wordBreak: 'break-all'
          }}
        >
          {call.url}
        </div>
      </div>

      {call.requestHeaders && renderJSONViewer(call.requestHeaders, 'Request Headers')}
      
      {call.requestBody && renderJSONViewer(call.requestBody, 'Request Body')}
      
      {call.responseHeaders && renderJSONViewer(call.responseHeaders, 'Response Headers')}
      
      {call.responseBody && renderJSONViewer(call.responseBody, 'Response Body')}

      {call.error && (
        <div style={{ marginTop: 8 }}>
          <Text type="danger" strong style={{ fontSize: '13px' }}>Error:</Text>
          <div
            style={{
              background: '#fff2f0',
              border: '1px solid #ffccc7',
              borderRadius: '4px',
              padding: '8px',
              marginTop: 4,
              fontSize: '12px',
              color: '#a8071a'
            }}
          >
            {call.error}
          </div>
        </div>
      )}
    </div>
  );

  const renderExecutionLogs = () => (
    <div style={{ marginBottom: 16 }}>
      {executionLogs.map((log) => (
        <div key={log.id} style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Text style={{ fontSize: '11px', color: '#999', minWidth: '60px' }}>
            {formatTimestamp(log.timestamp)}
          </Text>
          <Tag 
            color={log.type === 'error' ? 'red' : log.type === 'success' ? 'green' : log.type === 'warning' ? 'orange' : 'blue'}
            style={{ fontSize: '10px', margin: 0 }}
          >
            {log.type.toUpperCase()}
          </Tag>
          <Text style={{ fontSize: '12px', flex: 1 }}>{log.message}</Text>
        </div>
      ))}
    </div>
  );

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space>
            <span>Execution Console</span>
            <Tag color="blue">
              <ClockCircleOutlined /> Total: {totalExecutionTime}ms
            </Tag>
            <Tag color="green">{httpCalls.length} HTTP calls</Tag>
          </Space>
          <Button
            size="small"
            icon={<ClearOutlined />}
            onClick={onClear}
            title="Clear console"
          >
            Clear
          </Button>
        </div>
      }
      style={{
        marginTop: 16,
        background: '#fafafa',
        border: '1px solid #d9d9d9'
      }}
      bodyStyle={{ 
        background: '#fff',
        maxHeight: '400px',
        overflow: 'auto',
        padding: '12px'
      }}
    >
      {executionLogs.length > 0 && (
        <>
          <Text strong style={{ fontSize: '14px' }}>Execution Logs:</Text>
          <Divider style={{ margin: '8px 0' }} />
          {renderExecutionLogs()}
        </>
      )}

      {httpCalls.length > 0 && (
        <>
          <Text strong style={{ fontSize: '14px' }}>HTTP Calls Details:</Text>
          <Divider style={{ margin: '8px 0' }} />
          
          <Collapse
            size="small"
            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            onChange={(keys) => setExpandedPanels(keys as string[])}
            activeKey={expandedPanels}
            style={{ background: 'transparent' }}
          >
            {httpCalls.map((call, index) => (
              <Panel
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color={getMethodColor(call.method)}>{call.method}</Tag>
                    <Tag color={getStatusColor(call.status)}>{call.status}</Tag>
                    <Text style={{ fontSize: '12px', color: '#666' }}>
                      {call.responseTime}ms
                    </Text>
                    <Text 
                      ellipsis={{ tooltip: call.url }} 
                      style={{ fontSize: '12px', maxWidth: '300px' }}
                    >
                      {call.url}
                    </Text>
                                         {!call.success && (
                       <Tag color="red">ERROR</Tag>
                     )}
                  </div>
                }
                key={call.id}
                style={{ marginBottom: 8 }}
              >
                {renderHttpCallDetails(call)}
              </Panel>
            ))}
          </Collapse>
        </>
      )}

      {httpCalls.length === 0 && executionLogs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
          <Text>No execution data available. Run a test to see results here.</Text>
        </div>
      )}
    </Card>
  );
}; 